import { useEffect, useRef } from 'react';
import RevolutCheckout from '@revolut/checkout';
import { getCompany, getRevolutConfig, createRevolutSubscription } from '../services/api';
import { shouldChargeGross } from '../utils/subscriptionBilling';
import { getSubscriptionPlans } from '../constants/subscriptionPlans';
import { PAYMENT_API_URL } from '../constants/links';

const PLAN_NAMES = {
  base: 'Base Business Plan',
  standard: 'Standard Business Plan',
};

const toMinorUnits = (amount) => Math.round(Number(amount) * 100);

const RevolutPayButton = ({ planKey, restaurant, currencies, email, authenticatedFetch }) => {
  const targetRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target || !PAYMENT_API_URL || !restaurant) return undefined;

    let cancelled = false;
    let payments;

    (async () => {
      const company = restaurant.registered_company_id
        ? await getCompany(restaurant.registered_company_id, authenticatedFetch).catch(() => null)
        : null;
      const config = await getRevolutConfig(authenticatedFetch);
      if (cancelled) return;

      const offer = getSubscriptionPlans(restaurant.currency, currencies);
      const plan = planKey === 'base' ? offer.base : offer.standard;
      const payable = shouldChargeGross(company) ? plan.gross : plan.net;
      const amount = toMinorUnits(payable);
      const planName = PLAN_NAMES[planKey];

      payments = await RevolutCheckout.payments({
        publicToken: config.public_key,
        mode: config.mode === 'prod' ? 'prod' : 'sandbox',
      });
      if (cancelled) {
        payments.destroy();
        return;
      }

      payments.revolutPay.mount(target, {
        currency: offer.currency,
        totalAmount: amount,
        buttonStyle: { size: 'large', height: '48px', radius: 'small', variant: 'dark', action: 'pay' },
        createOrder: async () => {
          try {
            const order = await createRevolutSubscription({
              amount,
              currency: offer.currency,
              description: `${planName} - ${restaurant.name}`,
              restaurant_id: restaurant.id,
              plan: planKey,
              customer_email: email || undefined,
              reference: `${restaurant.id}-${planKey}`,
              redirect_url: window.location.href,
            }, authenticatedFetch);
            return { publicId: order.token };
          } catch (err) {
            alert(err.message || 'Failed to open Revolut.');
            throw err;
          }
        },
      });
    })().catch((err) => {
      if (!cancelled) console.error(err);
    });

    return () => {
      cancelled = true;
      payments?.destroy();
    };
  }, [planKey, restaurant, currencies, email, authenticatedFetch]);

  return <div ref={targetRef} className="revolut-pay-slot ph-no-capture" />;
};

export default RevolutPayButton;
